import { Trans, t } from "@lingui/macro";
import Button from "components/Button/Button";
import { useAffiliateCodes } from "domain/referrals/hooks";
import { Token } from "domain/tokens";

import { TrackingLink } from "components/TrackingLink/TrackingLink";
import { toJpeg } from "html-to-image";
import shareBgImg from "img/position-share-bg.png";
import downloadImage from "lib/downloadImage";
import { helperToast } from "lib/helperToast";
import { getRootShareApiUrl, getTwitterIntentURL } from "lib/legacy";
import useLoadImage from "lib/useLoadImage";
import { userAnalytics } from "lib/userAnalytics";
import { SharePositionActionEvent } from "lib/userAnalytics/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { BiCopy } from "react-icons/bi";
import { FiTwitter } from "react-icons/fi";
import { RiFileDownloadLine } from "react-icons/ri";
import { useCopyToClipboard } from "react-use";
import Modal from "../Modal/Modal";
import "./PositionShare.css";
import { PositionShareCard } from "./PositionShareCard";
// Share images are hosted by the MoneyX copy-trading API (api.moneyxpro.com).
const SHARE_API_BASE = "https://api.moneyxpro.com";
const UPLOAD_URL = SHARE_API_BASE + "/api/copy/share";
const config = { quality: 0.95, canvasWidth: 518, canvasHeight: 292, type: "image/jpeg" };

function getShareURL(imageInfo) {
  return imageInfo ? imageInfo.fullUrl : undefined;
}

type Props = {
  entryPrice: bigint | undefined;
  indexToken: Token;
  isLong: boolean;
  leverage: bigint | undefined;
  markPrice: bigint;
  pnlAfterFeesPercentage: bigint;
  setIsPositionShareModalOpen: (isOpen: boolean) => void;
  isPositionShareModalOpen: boolean;
  account: string | undefined | null;
  chainId: number;
};

function PositionShare({
  entryPrice,
  indexToken,
  isLong,
  leverage,
  markPrice,
  pnlAfterFeesPercentage,
  setIsPositionShareModalOpen,
  isPositionShareModalOpen,
  account,
  chainId,
}: Props) {
  const userAffiliateCode = useAffiliateCodes(chainId, account);
  const [uploadedImageInfo, setUploadedImageInfo] = useState<any>();
  const [uploadedImageError, setUploadedImageError] = useState<string | null>(null);
  const [, copyToClipboard] = useCopyToClipboard();
  const sharePositionBgImg = useLoadImage(shareBgImg);
  const cardRef = useRef<HTMLDivElement>(null);
  const tweetLink = getTwitterIntentURL(
    `Latest $${indexToken?.symbol} trade on @bescllc`,
    getShareURL(uploadedImageInfo)
  );

  // Render the card to a JPEG and upload it so we get a public image URL to
  // copy/tweet. toJpeg is called a few times so the background image is fully
  // painted before capture (html-to-image quirk).
  useEffect(() => {
    let cancelled = false;
    (async function () {
      const element = cardRef.current;
      if (!element || !sharePositionBgImg) return;
      try {
        const image = await toJpeg(element, config)
          .then(() => toJpeg(element, config))
          .then(() => toJpeg(element, config));
        const res = await fetch(UPLOAD_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ image }),
        }).then((r) => r.json());
        if (cancelled) return;
        if (res && res.url) {
          setUploadedImageInfo({ id: res.id, fullUrl: SHARE_API_BASE + res.url });
        } else {
          setUploadedImageInfo(null);
          setUploadedImageError(t`Image generation error, please refresh and try again.`);
        }
      } catch (error) {
        if (cancelled) return;
        setUploadedImageInfo(null);
        setUploadedImageError(t`Image generation error, please refresh and try again.`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sharePositionBgImg]);

  async function handleDownload() {
    const element = cardRef.current;
    if (!element) return;
    userAnalytics.pushEvent<SharePositionActionEvent>({
      event: "SharePositionAction",
      data: {
        action: "Download",
      },
    });

    const imgBlob = await toJpeg(element, config)
      .then(() => toJpeg(element, config))
      .then(() => toJpeg(element, config));
    downloadImage(imgBlob, "share.jpeg");
  }

  function handleCopy() {
    if (!uploadedImageInfo) {
      helperToast.error(t`Preparing your share image — try again in a moment.`);
      return;
    }
    userAnalytics.pushEvent<SharePositionActionEvent>({
      event: "SharePositionAction",
      data: { action: "Copy" },
    });
    copyToClipboard(uploadedImageInfo.fullUrl);
    helperToast.success(t`Image link copied to clipboard.`);
  }

  const trackShareTwitter = useCallback(() => {
    userAnalytics.pushEvent<SharePositionActionEvent>(
      {
        event: "SharePositionAction",
        data: {
          action: "ShareTwitter",
        },
      },
      { instantSend: true }
    );
  }, []);

  return (
    <Modal
      className="position-share-modal"
      isVisible={isPositionShareModalOpen}
      setIsVisible={setIsPositionShareModalOpen}
      label={t`Share Position`}
    >
      <PositionShareCard
        entryPrice={entryPrice}
        indexToken={indexToken}
        isLong={isLong}
        leverage={leverage}
        markPrice={markPrice}
        pnlAfterFeesPercentage={pnlAfterFeesPercentage}
        userAffiliateCode={userAffiliateCode}
        ref={cardRef}
        loading={!uploadedImageInfo && !uploadedImageError}
        sharePositionBgImg={sharePositionBgImg}
      />
      {uploadedImageError && <span className="error">{uploadedImageError}</span>}

      <div className="actions">
        <Button variant="secondary" disabled={!uploadedImageInfo} className="mr-15" onClick={handleCopy}>
          <BiCopy className="icon" />
          <Trans>Copy</Trans>
        </Button>
        <Button variant="secondary" className="mr-15" onClick={handleDownload}>
          <RiFileDownloadLine className="icon" />
          <Trans>Download</Trans>
        </Button>
        <TrackingLink onClick={trackShareTwitter}>
          <Button newTab variant="secondary" disabled={!uploadedImageInfo} className="mr-15" to={tweetLink}>
            <FiTwitter className="icon" />
            <Trans>Tweet</Trans>
          </Button>
        </TrackingLink>
      </div>
    </Modal>
  );
}

export default PositionShare;
